import { BehaviorSubject } from "rxjs";
import { authHeader } from "../helpers/auth-header";
import { handleApiRequest } from "../helpers/api-request";
import authConfig from "../configs/auth";
import { Log } from "../hooks/logger";

const getUserSubject = () => {
  let currentUserSubject = new BehaviorSubject(null);
  if (typeof window !== "undefined" && localStorage) {
    currentUserSubject = new BehaviorSubject(
      JSON.parse(localStorage.getItem("currentUser"))
    );
  }
  return currentUserSubject;
};

const handleLoginResult = (result) => {
  if (result.user) {
    const user = result.user;
    user.token = result.token;
    user.refreshToken = result.refresh;
    localStorage.setItem("currentUser", JSON.stringify(user));
    getUserSubject().next(user);
    return result;
  } else {
    return Promise.reject(result.reason);
  }
};

const login = (params) => {
  const { username, password, rememberMe } = params;
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  };

  return handleApiRequest(authConfig.loginEndpoint, requestOptions, {
    noRetry: true,
  }).then(handleLoginResult);
};

const register = (params) => {
  const { firstName, lastName, email, password } = params;
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ firstName, lastName, email, password })
  };

  return handleApiRequest(authConfig.registerEndpoint, requestOptions, {
    noRetry: true,
  });
};

const logout = () => {
  localStorage.removeItem("currentUser");
  getUserSubject().next(null);
};

const refreshUser = () => {
  return handleApiRequest(authConfig.user, { headers: authHeader() }).then(
    (user) => {
      const oldUser = getUserSubject().value;
      user = { ...oldUser, ...user };
      getUserSubject().next(user);
      return user;
    }
  );
};

const activateToken = (token) => {
  return handleApiRequest(
    authConfig.activateToken,
    {
      method: "POST",
      body: JSON.stringify({ token }),
      headers: { "Content-Type": "application/json" },
    },
    { parseBodyAsJson: false, noRetry: true }
  ).then((response) => {
    return response.ok;
  });
};

const requestPasswordChange = (email) => {
  return handleApiRequest(
    `${authConfig.passwordChangeRequest}&email=${email}`,
    { method: "POST" },
    { parseBodyAsJson: false, noRetry: true }
  )
    .then(() => true)
    .catch(() => false);
};

const performPasswordChange = (token, newPassword) => {
  const requestOptions = {
    method: "POST",
    body: JSON.stringify({ token, newPassword }),
    headers: { "Content-Type": "application/json" },
  };

  return handleApiRequest(authConfig.passwordChangePerform, requestOptions, {
    parseBodyAsJson: false,
    noRetry: true,
  })
    .then(() => true)
    .catch(() => false);
};

const refreshLogin = async () => {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      userID: getUserSubject().value?.id,
      token: getUserSubject().value?.refreshToken,
    }),
  };

  try {
    const result_1 = await handleApiRequest(
      authConfig.userRefresh,
      requestOptions,
      { noRetry: true }
    );
    return handleLoginResult(result_1);
  } catch (reason) {
    Log("Refresh failed: " + reason, "warn");
    logout();
  }
};

const setSchedule = (params) => {
  const { payload } = params;
  const requestOptions = {
    method: "POST",
    headers: {
          'Content-Type': 'application/json',
          ...authHeader()
        },
    body: JSON.stringify({ isSchool: payload.isSchool, duration: payload.duration, fromDate: payload.fromDate, toDate: payload.toDate, availability: payload.availability }),
  };

  return handleApiRequest(authConfig.setScheduleEndpoint, requestOptions, {
    noRetry: true,
  }) 
  .then(() => true)
  .catch(() => false);
};

const getSlotsByDate = (params) => {
  const { payload } = params;
  const requestOptions = {
    method: "POST",
    headers: {
          'Content-Type': 'application/json',
          ...authHeader()
        },
    body: JSON.stringify({ date: payload.date }),
  };

  return handleApiRequest(authConfig.getSlotsByDate, requestOptions, {
    noRetry: true,
  }) 
  .then(() => true)
  .catch(() => false);
};

export const authService = {
  login,
  register,
  logout,
  refreshUser,
  activateToken,
  requestPasswordChange,
  performPasswordChange,
  refreshLogin,
  setSchedule,
  getSlotsByDate,
  currentUser: getUserSubject().asObservable(),
  get currentUserValue() {
    return getUserSubject().value;
  },
};
